import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import {
  getTotalNotificationCount,
  deleteNotificationsByDateRange,
} from "../../../../../../../services/notificationServices";

import {
  getTotalMessageCount,
  deleteMessagesByDateRange,
} from "../../../../../../../services/messageServices";

import {
  buildConfirmMessage,
  getDefaultDateRangeForm,
  validateDateRangeForm,
} from "../utils/communicationSettingsUtils";

export function useCommunicationSettings() {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const [notificationCount, setNotificationCount] = useState(0);
  const [messageCount, setMessageCount] = useState(0);

  const [notificationsForm, setNotificationsForm] = useState(
    getDefaultDateRangeForm()
  );

  const [messagesForm, setMessagesForm] = useState(getDefaultDateRangeForm());

  const [submittingNotifications, setSubmittingNotifications] = useState(false);
  const [submittingMessages, setSubmittingMessages] = useState(false);

  const totalCount = useMemo(() => {
    return Number(notificationCount || 0) + Number(messageCount || 0);
  }, [notificationCount, messageCount]);

  const fetchCounts = useCallback(async () => {
    try {
      setRefreshing(true);

      const [notifications, messages] = await Promise.all([
        getTotalNotificationCount(),
        getTotalMessageCount(),
      ]);

      setNotificationCount(Number(notifications) || 0);
      setMessageCount(Number(messages) || 0);
    } catch (error) {
      console.error("Erreur chargement compteurs communication :", error);
      toast.error("Impossible de charger les compteurs communication.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchCounts();
  }, [fetchCounts]);

  const setNotificationsField = (field, value) => {
    setNotificationsForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const setMessagesField = (field, value) => {
    setMessagesForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handlePurgeNotifications = async (event) => {
    event.preventDefault();

    const validationError = validateDateRangeForm(notificationsForm);

    if (validationError) {
      toast.error(validationError);
      return;
    }

    const ok = window.confirm(
      buildConfirmMessage({
        label: "notifications",
        startDate: notificationsForm.startDate,
        endDate: notificationsForm.endDate,
      })
    );

    if (!ok) return;

    try {
      setSubmittingNotifications(true);

      const result = await deleteNotificationsByDateRange(
        notificationsForm.startDate,
        notificationsForm.endDate
      );

      toast.success(result?.message || "Notifications supprimées.");

      await fetchCounts();
    } catch (error) {
      console.error("Erreur suppression notifications :", error);
      toast.error("Erreur lors de la suppression des notifications.");
    } finally {
      setSubmittingNotifications(false);
    }
  };

  const handlePurgeMessages = async (event) => {
    event.preventDefault();

    const validationError = validateDateRangeForm(messagesForm);

    if (validationError) {
      toast.error(validationError);
      return;
    }

    const ok = window.confirm(
      buildConfirmMessage({
        label: "messages",
        startDate: messagesForm.startDate,
        endDate: messagesForm.endDate,
      })
    );

    if (!ok) return;

    try {
      setSubmittingMessages(true);

      const result = await deleteMessagesByDateRange(
        messagesForm.startDate,
        messagesForm.endDate
      );

      toast.success(result?.message || "Messages supprimés.");

      await fetchCounts();
    } catch (error) {
      console.error("Erreur suppression messages :", error);
      toast.error("Erreur lors de la suppression des messages.");
    } finally {
      setSubmittingMessages(false);
    }
  };

  return {
    loading,
    refreshing,

    notificationCount,
    messageCount,
    totalCount,

    notificationsForm,
    messagesForm,
    setNotificationsField,
    setMessagesField,

    submittingNotifications,
    submittingMessages,

    fetchCounts,
    handlePurgeNotifications,
    handlePurgeMessages,
  };
}